"use client";

import { Suspense, useEffect, useState } from "react";
import Image from "next/image";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import {
  clearAccessToken,
  getAccessToken,
  safeLoginNext,
  saveLoginNext,
  setAccessToken,
} from "@/lib/session";
import { getAuthMe, loginWithPassword, resendVerificationEmail } from "@/lib/api";
import { useAuth } from "@/components/AuthContext";

/** The structured 403 body the login route returns for an unverified account. */
type LoginErrorDetail = string | { code?: string; message?: string };

function LoginInner() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { refresh } = useAuth();
  const next = safeLoginNext(searchParams.get("next"));
  const [already, setAlready] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Email + password state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // A failed Google sign-in bounces back here with the reason: the OAuth
  // callback is a top-level navigation, so it redirects rather than returning
  // JSON we could catch.
  const [error, setError] = useState<string | null>(
    searchParams.get("auth_error"),
  );
  const [busy, setBusy] = useState(false);
  // True when login failed *only* because the address is unverified — we show
  // a resend affordance instead of a dead-end error.
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);

  // Google refused this address because it has no CRAYDL account (rather than,
  // say, an archived company) — the one failure whose fix is "go sign up".
  const noAccount = /domain not allowed/i.test(searchParams.get("auth_error") ?? "");

  useEffect(() => {
    if (!getAccessToken()) {
      setCheckingSession(false);
      return;
    }
    let cancelled = false;
    void getAuthMe()
      .then(() => {
        if (!cancelled) setAlready(Boolean(getAccessToken()));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if ((err as { response?: { status?: number } })?.response?.status === 401) {
          clearAccessToken();
        } else {
          setError("Could not verify your session. Please try signing in again.");
        }
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setUnverified(false);
    setResent(false);
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const result = await loginWithPassword({ email: email.trim(), password });
      setAccessToken(result.access_token);
      await refresh();
      router.replace(next);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: LoginErrorDetail } } })
        ?.response?.data?.detail;
      // The unverified-account detail is an object, not a string — rendering it
      // raw would print "[object Object]".
      if (typeof detail === "object" && detail?.code === "email_unverified") {
        setUnverified(true);
        setError(detail.message ?? "Please verify your email to activate your account.");
      } else {
        setError(typeof detail === "string" ? detail : "Invalid email or password.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleResend() {
    setBusy(true);
    try {
      await resendVerificationEmail({ email: email.trim() });
      setResent(true);
    } finally {
      setBusy(false);
    }
  }

  if (checkingSession) {
    return <div className="text-center text-ink-faint">Checking your session…</div>;
  }

  if (already) {
    return (
      <div className="text-center space-y-4">
        <p className="text-ink-soft">You already have a session.</p>
        <Link
          href={next}
          className="inline-block px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium"
        >
          Continue to app
        </Link>
        <button
          type="button"
          onClick={() => {
            clearAccessToken();
            setAlready(false);
            void refresh();
          }}
          className="block mx-auto text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          Sign out
        </button>
      </div>
    );
  }

  const startUrl = `/api/auth/google/start`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-card">
      <div className="max-w-sm w-full text-center space-y-8 p-8">
        <div className="flex justify-center">
          <Image
            src="/logo.png"
            alt="CRAYDL"
            width={220}
            height={72}
            className="h-16 w-auto object-contain"
            priority
          />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-ink">Welcome back</h1>
          <p className="text-sm text-ink-faint">Sign in to your account to continue</p>
        </div>

        {/* Google OAuth — staff users */}
        <a
          href={startUrl}
          onClick={() => saveLoginNext(next)}
          className="inline-flex items-center justify-center gap-3 w-full py-3 px-4 bg-card border border-outline-strong rounded-lg text-sm font-medium text-ink shadow-sm hover:bg-field-hover hover:shadow transition-all"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </a>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <hr className="flex-1 border-outline-strong" />
          <span className="text-xs text-ink-faint uppercase">or</span>
          <hr className="flex-1 border-outline-strong" />
        </div>

        {/* Email + password — customer portal users */}
        <form onSubmit={handlePasswordLogin} className="space-y-3 text-left">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              required
              className="bg-field w-full px-4 py-2.5 border border-outline-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="bg-field w-full px-4 py-2.5 border border-outline-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div className="text-right">
            <Link
              href={
                email.trim()
                  ? `/forgot-password?email=${encodeURIComponent(email.trim())}`
                  : "/forgot-password"
              }
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Forgot your password?
            </Link>
          </div>

          {error && (
            <div className="text-sm text-danger-ink bg-danger-soft border border-danger rounded-lg px-3 py-2 space-y-1">
              <p>{error}</p>
              {/* The dead end this replaces: someone with no CRAYDL account
                  clicks Google, gets refused, and has nowhere to go. */}
              {noAccount && (
                <p className="text-danger-ink">
                  Don&apos;t have an account yet?{" "}
                  <Link href="/signup" className="font-medium underline">
                    Create one
                  </Link>
                  .
                </p>
              )}
            </div>
          )}

          {unverified &&
            (resent ? (
              <p className="text-sm text-success-ink bg-success-soft border border-success rounded-lg px-3 py-2">
                Verification email sent. Check your inbox.
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={busy}
                className="w-full py-2.5 px-4 border border-outline-strong text-ink-soft text-sm font-medium rounded-lg hover:bg-card transition-colors disabled:opacity-50"
              >
                {busy ? "Sending..." : "Resend verification email"}
              </button>
            ))}

          <button
            type="submit"
            disabled={busy}
            className="w-full py-2.5 px-4 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {busy ? "Signing in..." : "Sign in with Email"}
          </button>
        </form>

        {/* New company self-serve onboarding */}
        <div className="pt-2 text-sm text-ink-faint">
          New to CRAYDL?{" "}
          <Link
            href="/signup"
            className="font-medium text-brand-600 hover:text-brand-700"
          >
            Create an account
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="text-center text-ink-faint">Loading...</div>}>
      <LoginInner />
    </Suspense>
  );
}
